import { computed, inject, Injectable, signal } from '@angular/core';
import { Tools } from 'src/app/classes/shared/tools';
import { WidgetV2 } from 'src/app/interfaces/api/chart';
import { Dashboard } from 'src/app/interfaces/shared/dashboard';
import { ChartService } from 'src/app/services/api/charts/chart.service';

/**
 * 🗄️ Store centralisé pour l'état des dashboards
 * Gère uniquement les données, pas les requêtes HTTP
 */
@Injectable({
    providedIn: 'root'
})

export class DashboardStore {
    // Services
    private readonly chartService: ChartService = inject(ChartService);
    private readonly tools: Tools = inject(Tools);

    // --- DASHBOARD --- //
    // Signals
    private _dashboards = signal<Dashboard[]>([]);
    readonly dashboards = this._dashboards.asReadonly();

    private _currentDashboardId = signal<string | null>(null);
    readonly currentDashboardId = this._currentDashboardId.asReadonly();

    private _currentProjectId = signal<string | null>(null);
    readonly currentProjectId = this._currentProjectId.asReadonly();

    private _isLoading = signal<boolean>(false);
    readonly isLoading = this._isLoading.asReadonly();

    private _error = signal<string | null>(null);
    readonly error = this._error.asReadonly();

    private _loadedProjectId = signal<string | null>(null);

    // Computeds
    readonly sortedDashboards = computed(() => {
        const dashboards = this._dashboards();
        const projectId = this._currentProjectId();

        if (!projectId) return dashboards;

        return [...dashboards].sort((a, b) => {
            const aIndex = a.projectSettings?.find((s: any) => s.project === projectId)?.index;
            const bIndex = b.projectSettings?.find((s: any) => s.project === projectId)?.index;

            if (aIndex !== undefined && bIndex !== undefined) {
                return aIndex - bIndex;
            } else if (aIndex !== undefined) {
                return -1;
            } else if (bIndex !== undefined) {
                return 1;
            } else {
                return (a.title || '').localeCompare(b.title || '');
            }
        });
    });

    readonly currentDashboard = computed(() => {
        const id = this._currentDashboardId();
        if (!id) return undefined;
        return this.sortedDashboards().find(d => d.id === id);
    });

    readonly count = computed(() => this._dashboards().length);

    // --- WIDGETS --- //
    // Signals
    private _widgetsCache = signal<Map<string, WidgetV2[]>>(new Map());

    constructor() { }

    // ----------- DASHBOARDS ----------- //

    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // 📝 UPDATE METHODS
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

    isProjectLoaded(projectId: string): boolean {
        return this._loadedProjectId() === projectId;
    }

    private markProjectLoaded(projectId: string): void {
        this._loadedProjectId.set(projectId);
    }

    setLoading(value: boolean): void {
        this._isLoading.set(value);
    }

    setDashboards(dashboards: Dashboard[], projectId?: string): void {
        const firstChart = dashboards[0]?.charts?.[0];
        console.log(`[STORE] setDashboards count=${dashboards.length} chartsType=${typeof firstChart}`);
        this._dashboards.set(dashboards);
        const targetProjectId = projectId ?? this._currentProjectId();

        if (targetProjectId) {
            this.markProjectLoaded(targetProjectId);
        }
    }

    async setCurrentDashboardId(id: string | null): Promise<void> {
        const previousId = this._currentDashboardId();

        if (previousId === id) {
            return;
        }

        this._currentDashboardId.set(id);

        if (!id) {
            return;
        }

        const dashboard = this.getDashboardById(id);

        if (!dashboard) {
            console.warn(`⚠️ Dashboard ${id} not found`);
            return;
        }

        await this.loadWidgetsForDashboard(id);
    }

    setCurrentProjectId(projectId: string | null): void {
        const previousProjectId = this._currentProjectId();

        if (previousProjectId && previousProjectId !== projectId) {
            this._dashboards.set([]);
            this._loadedProjectId.set(null);
            this._currentDashboardId.set(null);
            this.clearWidgetsCache();
        }

        this._currentProjectId.set(projectId);
    }

    upsertDashboards(dashboards: Dashboard[]): void {
        const current = this._dashboards();
        const projectId = this._currentProjectId();

        const relevantDashboards = projectId
            ? dashboards.filter(d => d.projects?.includes(projectId))
            : dashboards;

        if (relevantDashboards.length === 0) {
            console.log('⚠️ No relevant dashboards to upsert for current project');
            return;
        }

        const updated = [...current];

        relevantDashboards.forEach(dashboard => {
            const index = updated.findIndex(d => d.id === dashboard.id);
            if (index >= 0) {
                const existing = updated[index]!;
                updated[index] = { ...dashboard, charts: existing.charts?.length ? existing.charts : dashboard.charts };
            } else {
                updated.push(dashboard);
            }
        });

        this._dashboards.set(updated);
    }

    addDashboard(dashboard: Dashboard): void {
        const current = this._dashboards();
        const exists = current.find(d => d.id === dashboard.id);

        if (exists) {
            this.updateDashboard(dashboard);
        } else {
            this._dashboards.set([...current, dashboard]);
        }
    }

    updateDashboard(updatedDashboard: Partial<Dashboard> & { id: string }): void {
        const current = this._dashboards();
        const index = current.findIndex(d => d.id === updatedDashboard.id);

        if (index < 0) return;

        const updated = [...current];
        const existing = updated[index]!;

        updated[index] = {
            ...existing,
            ...updatedDashboard,
            projectSettings: updatedDashboard.projectSettings ?? existing.projectSettings,
            charts: updatedDashboard.charts ?? existing.charts,
            projects: updatedDashboard.projects ?? existing.projects,
        };

        this._dashboards.set(updated);
    }

    mergeDashboards(partialDashboards: Partial<Dashboard>[] | any): void {
        if (!Array.isArray(partialDashboards)) {
            console.warn('⚠️ mergeDashboards received non-array:', partialDashboards);
            return;
        }

        const current = this._dashboards();
        const updated = current.map(dashboard => {
            const partial = partialDashboards.find(
                p => (p as any).id === dashboard.id || (p as any)._id === dashboard.id
            );

            if (!partial) return dashboard;

            return {
                ...dashboard,
                ...partial,
                projectSettings: partial.projectSettings ?? dashboard.projectSettings,
                charts: dashboard.charts?.length ? dashboard.charts : partial.charts,
                projects: partial.projects ?? dashboard.projects,
            };
        });

        this._dashboards.set(updated);
    }

    removeDashboard(dashboardId: string | string[]): void {
        const ids = Array.isArray(dashboardId) ? dashboardId : [dashboardId];
        const current = this._dashboards();
        const filtered = current.filter(d => !ids.includes(d.id));
        this._dashboards.set(filtered);
    }

    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // GET
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

    getDashboardById(id: string): Dashboard | undefined {
        return this._dashboards().find(d => d.id === id);
    }

    getCurrentDashboard(): Dashboard | undefined {
        return this.currentDashboard();
    }

    getAllDashboards(): Dashboard[] {
        return this._dashboards();
    }

    hasDashboard(id: string): boolean {
        return this._dashboards().some(d => d.id === id);
    }

    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // 🧹 UTILITIES
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

    reset(): void {
        this._dashboards.set([]);
        this._currentDashboardId.set(null);
        this._currentProjectId.set(null);
        this._isLoading.set(false);
        this._loadedProjectId.set(null);
    }

    // ----------- WIDGETS ----------- //

    /**
     * Le temps réel sur 'Dashboard' (listenToUpdates) ne livre que des IDs de charts
     * bruts (le document Mongo), jamais les WidgetV2 hydratés — ceux-ci ne sont
     * chargés qu'une fois, via loadWidgetsForDashboard(), et mis en cache par
     * dashboardId. Sans ce point d'entrée, un chart créé/modifié/supprimé par un
     * autre utilisateur ne serait jamais reflété : le cache resterait périmé
     * indéfiniment pour tout le monde sauf l'auteur (qui invalide le sien localement
     * à la fermeture de l'éditeur). À appeler à chaque update temps réel reçu pour 'Dashboard'.
     */
    async syncWidgetsForRealtimeUpdate(dashboard: Partial<Dashboard> & { id?: string, _id?: string }): Promise<void> {
        const dashboardId = dashboard.id ?? dashboard._id;

        if (!dashboardId || !dashboard.charts) {
            return;
        }

        const incomingIds = dashboard.charts
            .map(c => this.tools.toId(c))
            .filter((id): id is string => !!id);

        const cachedWidgets = this._widgetsCache().get(dashboardId);
        const cachedIds = (cachedWidgets ?? [])
            .map(w => w.id)
            .filter((id): id is string => !!id);

        const changed = incomingIds.length !== cachedIds.length
            || incomingIds.some(id => !cachedIds.includes(id));

        if (!changed) {
            return;
        }

        if (!cachedWidgets) {
            if (this._currentDashboardId() === dashboardId) {
                await this.loadWidgetsForDashboard(dashboardId);
            }
            return;
        }

        const addedIds = incomingIds.filter(id => !cachedIds.includes(id));
        let newWidgets: WidgetV2[] = [];

        if (addedIds.length) {
            const { firstValueFrom } = await import('rxjs');
            newWidgets = await firstValueFrom(this.chartService.getChartsForDashboardBatch(addedIds));
        }

        const merged = incomingIds
            .map(id => cachedWidgets.find(w => w.id === id) ?? newWidgets.find(w => w.id === id))
            .filter((w): w is WidgetV2 => !!w);

        this.saveWidgetsToCache(dashboardId, merged);

        if (this._currentDashboardId() === dashboardId) {
            const currentDashboard = this.getDashboardById(dashboardId);

            if (currentDashboard) {
                this.updateDashboard({ ...currentDashboard, id: dashboardId, charts: merged });
            }
        }
    }

    /**
     * Éditer le contenu d'un chart déjà affiché (titre, design, requête...) ne
     * touche jamais au tableau `charts` du Dashboard — contrairement à
     * l'ajout/suppression, cette update arrive donc sur la collection Chart
     * elle-même, pas Dashboard. On met à jour le widget déjà en cache par
     * mutation (pas de remplacement d'objet, pour conserver `signalSingled`),
     * puis on déclenche son propre signal de refresh manuel pour que
     * WidgetDirective recalcule sa donnée avec la requête à jour.
     */
    mergeChartUpdate(chart: Partial<WidgetV2> & { id?: string, _id?: string }): void {
        const chartId = chart.id ?? chart._id;

        if (!chartId) {
            return;
        }

        this._widgetsCache().forEach((widgets, dashboardId) => {
            const existing = widgets.find(w => w.id === chartId);
            if (!existing) {
                return;
            }

            Object.assign(existing, chart);
            existing.signalSingled?.set(`refresh_manuel_${Date.now()}`);

            if (this._currentDashboardId() === dashboardId) {
                const currentDashboard = this.getDashboardById(dashboardId);

                if (currentDashboard) {
                    this.updateDashboard({ ...currentDashboard, id: dashboardId, charts: [...widgets] });
                }
            }
        });
    }

    /** Le widget enregistré par l'éditeur (nouvel objet) à la place de l'ancien dans le cache. */
    replaceWidget(widget: WidgetV2): void {
        if (!widget?.id) return;

        const cache = new Map(this._widgetsCache());
        cache.forEach((widgets, dashboardId) => {
            if (widgets.some(w => w.id === widget.id))
                cache.set(dashboardId, widgets.map(w => w.id === widget.id ? widget : w));
        });
        this._widgetsCache.set(cache);
    }

    private async loadWidgetsForDashboard(dashboardId: string): Promise<void> {
        const dashboard = this.getDashboardById(dashboardId);
        if (!dashboard) return;

        this.setLoading(true);
        this._error.set(null);

        const cache = this._widgetsCache();
        if (cache.has(dashboardId)) {
            const cachedWidgets = cache.get(dashboardId)!;

            this.updateDashboard({
                ...dashboard,
                id: dashboard.id,
                charts: cachedWidgets
            });

            this.setLoading(false);
            return;
        }

        if (!dashboard.charts || dashboard.charts.length === 0) {
            this.saveWidgetsToCache(dashboardId, []);
            this.setLoading(false);
            return;
        }

        const firstChart = dashboard.charts[0];

        if (typeof firstChart === 'object' && firstChart !== null && 'type' in firstChart) {
            this.saveWidgetsToCache(dashboardId, dashboard.charts as WidgetV2[]);
            this.setLoading(false);
            return;
        }

        if (typeof firstChart === 'string') {
            const chartIds = dashboard.charts as string[];

            try {
                const { firstValueFrom } = await import('rxjs');
                const widgets = await firstValueFrom(
                    this.chartService.getChartsForDashboardBatch(chartIds)
                );

                const sortedWidgets = chartIds
                    .map(id => widgets.find(w => w.id === id || (w as any)._id === id))
                    .filter(Boolean) as WidgetV2[];

                this.updateDashboard({
                    ...dashboard,
                    id: dashboard.id,
                    charts: sortedWidgets
                });

                this.saveWidgetsToCache(dashboardId, sortedWidgets);
            } catch (err) {
                console.error(`❌ Failed to load widgets for dashboard ${dashboardId}:`, err);
                this._error.set(err instanceof Error ? err.message : 'Failed to load widgets');

                this.updateDashboard({
                    ...dashboard,
                    id: dashboard.id,
                    charts: []
                });
            } finally {
                this.setLoading(false);
            }

            return;
        }

        this.updateDashboard({
            ...dashboard,
            id: dashboard.id,
            charts: []
        });

        this.setLoading(false);
    }

    /**
    * 💾 Sauvegarde les widgets dans le cache
    */
    private saveWidgetsToCache(dashboardId: string, widgets: WidgetV2[]): void {
        const cache = new Map(this._widgetsCache());
        cache.set(dashboardId, widgets);
        this._widgetsCache.set(cache);
    }

    /**
     * 🗑️ Supprime les widgets du cache pour un dashboard
     */
    private removeWidgetsFromCache(dashboardId: string): void {
        const cache = new Map(this._widgetsCache());
        cache.delete(dashboardId);
        this._widgetsCache.set(cache);
    }

    /**
     * ♻️ Invalide le cache d'un dashboard (force reload)
     */
    invalidateWidgetsCacheForDashboard(dashboardId: string): void {
        this.removeWidgetsFromCache(dashboardId);
    }

    /**
     * 🧹 Vide tout le cache widgets
     */
    private clearWidgetsCache(): void {
        this._widgetsCache.set(new Map());
    }
} 